import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  styled,
  Avatar
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Cake as CakeIcon,
  Height as HeightIcon,
  Scale as ScaleIcon,
  LocalHospital as HospitalIcon,
  Medication as MedicationIcon,
  Warning as WarningIcon,
  LocalBar as AlcoholIcon,
  Favorite as HeartIcon,
  Speed as SpeedIcon,
  Thermostat as TemperatureIcon,
  BloodtypeOutlined as BloodtypeIcon,
  Straighten as WaistIcon,
  MonitorHeart as OxygenIcon
} from '@mui/icons-material';
import SmokingRoomsIcon from '@mui/icons-material/SmokingRooms';
import agthealthService from '../../../services/agthealthService';
import PatientModal from './PatientModal';

// Importar sidebar y header compartidos
import Sidebar from '../../common/sidebar/Sidebar';
import DashboardHeader from '../../common/header/DashboardHeader';

// Definir color verde personalizado
const appleGreen = '#4CAF50';

const DrawerWidth = 240;

const Main = styled(Box)(({ theme, open, isMobile }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  paddingTop: 0,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: isMobile ? 0 : `-${DrawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    paddingTop: 0,
  }
}));

// Componentes estilizados para el estilo Apple
const AppleCard = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  border: '1px solid rgba(224, 224, 224, 0.8)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    boxShadow: '0 6px 25px rgba(0, 0, 0, 0.09)',
    transform: 'translateY(-2px)'
  }
}));

const AppleButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.2, 2.5),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.95rem',
  backgroundColor: variant === 'contained' ? appleGreen : 'transparent',
  color: variant === 'contained' ? 'white' : appleGreen,
  border: variant === 'outlined' ? `1px solid ${appleGreen}` : 'none',
  '&:hover': {
    backgroundColor: variant === 'contained' 
      ? '#3d8b40' // Slightly darker green on hover
      : 'rgba(76, 175, 80, 0.1)',
  },
}));

const AppleChip = styled(Chip)(({ theme, color }) => {
  const chipColors = {
    primary: {
      bg: 'rgba(76, 175, 80, 0.1)',
      color: appleGreen,
      border: appleGreen
    },
    warning: {
      bg: 'rgba(255, 152, 0, 0.1)',
      color: '#FF9800',
      border: '#FF9800'
    },
    secondary: {
      bg: 'rgba(156, 39, 176, 0.1)',
      color: '#9C27B0',
      border: '#9C27B0'
    }
  };
  
  const colorSet = chipColors[color] || chipColors.primary;
  
  return {
    backgroundColor: colorSet.bg,
    color: colorSet.color,
    border: `1px solid ${colorSet.border}`,
    borderRadius: '8px',
    '& .MuiChip-icon': {
      color: colorSet.color
    }
  };
});

const InfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const InfoIcon = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: '10px',
  backgroundColor: 'rgba(76, 175, 80, 0.1)',
  color: appleGreen,
}));

const InfoContent = styled(Box)(({ theme }) => ({
  flex: 1,
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.85rem',
  color: '#86868b',
  marginBottom: theme.spacing(0.5),
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#1d1d1f',
  fontWeight: 500,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 600,
  color: '#1d1d1f',
  marginBottom: theme.spacing(2.5),
}));

const SubSectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 600,
  color: '#1d1d1f',
  marginBottom: theme.spacing(1.5),
}));

const PatientAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  backgroundColor: 'rgba(76, 175, 80, 0.1)',
  color: appleGreen,
  fontSize: '2.5rem',
  fontWeight: 500,
  marginBottom: theme.spacing(2),
}));

const PatientDetail = () => {
  const { patientId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [user, setUser] = useState({
    firstName: 'Dr.',
    lastName: 'Usuario',
    specialty: 'Profesional de la salud'
  });
  
  // Estado para el modal de pacientes
  const [patientModal, setPatientModal] = useState({
    open: false,
    patientId: null
  });
  
  useEffect(() => {
    const fetchPatient = async () => {
      try {
        setLoading(true);
        const response = await agthealthService.getPatientById(patientId);
        setPatient(response.data);
        setError(null);
      } catch (err) {
        console.error('Error al cargar datos del paciente:', err);
        setError('No se pudieron cargar los datos del paciente. Por favor, inténtalo de nuevo.');
      } finally {
        setLoading(false);
      }
    };
    
    // Cargar información del usuario
    const fetchUserProfile = async () => {
      try {
        const response = await agthealthService.getUserProfile();
        if (response.data) {
          setUser(response.data);
        }
      } catch (error) {
        console.error('Error al cargar perfil de usuario:', error);
      }
    };
    
    fetchPatient();
    fetchUserProfile();
  }, [patientId]);
  
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const handleLogout = async () => {
    try {
      await agthealthService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };
  
  const handleSettings = () => {
    navigate('/settings');
  };
  
  // Función para abrir el modal de nuevo paciente
  const handleNewPatient = () => {
    setPatientModal({
      open: true,
      patientId: null
    });
  };

  // Función para abrir el modal de edición de paciente
  const handleEditPatient = () => {
    setPatientModal({
      open: true,
      patientId: patientId
    });
  };

  // Función para cerrar el modal
  const handleClosePatientModal = () => {
    setPatientModal({
      ...patientModal,
      open: false
    });
  };
  
  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };
  
  const content = loading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
      <CircularProgress style={{ color: appleGreen }} />
    </Box>
  ) : error ? (
    <Box sx={{ p: 3 }}>
      <Alert severity="error">{error}</Alert>
      <AppleButton
        sx={{ mt: 2 }}
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/patients')}
      >
        Volver a la lista
      </AppleButton>
    </Box>
  ) : !patient ? (
    <Box sx={{ p: 3 }}>
      <Alert severity="warning">No se encontró el paciente solicitado</Alert>
      <AppleButton
        sx={{ mt: 2 }}
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/patients')}
      >
        Volver a la lista
      </AppleButton>
    </Box>
  ) : (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <AppleButton
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/patients')}
        >
          Volver a pacientes
        </AppleButton>
        <AppleButton
          variant="contained"
          startIcon={<EditIcon />}
          onClick={handleEditPatient}
        >
          Editar Paciente
        </AppleButton>
      </Box>
      
      {/* Tarjeta de información personal */}
      <AppleCard>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <PatientAvatar>
            {getInitials(patient.firstName, patient.lastName)}
          </PatientAvatar>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
            {patient.firstName} {patient.lastName}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {patient.identificationNumber} ({patient.countryCode.toUpperCase()})
          </Typography>
        </Box>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            {/* Siempre mostrar el teléfono */}
            <InfoItem>
              <InfoIcon>
                <PhoneIcon />
              </InfoIcon>
              <InfoContent>
                <InfoLabel>Teléfono</InfoLabel>
                <InfoValue>{patient.phoneNumber || "No registrado"}</InfoValue>
              </InfoContent>
            </InfoItem>
            
            {/* Siempre mostrar el email */}
            <InfoItem>
              <InfoIcon>
                <EmailIcon />
              </InfoIcon>
              <InfoContent>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>{patient.email || "No registrado"}</InfoValue>
              </InfoContent>
            </InfoItem>
          </Grid>
          
          <Grid item xs={12} md={6}>
            {/* Siempre mostrar fecha de nacimiento */}
            <InfoItem>
              <InfoIcon>
                <CakeIcon />
              </InfoIcon>
              <InfoContent>
                <InfoLabel>Fecha de Nacimiento</InfoLabel>
                <InfoValue>
                  {patient.birthDate ? new Date(patient.birthDate).toLocaleDateString() : "No registrada"}
                </InfoValue>
              </InfoContent>
            </InfoItem>
            
            {/* Siempre mostrar edad y género */}
            <InfoItem>
              <InfoIcon>
                <PersonIcon />
              </InfoIcon>
              <InfoContent>
                <InfoLabel>Edad / Género</InfoLabel>
                <InfoValue>
                  {patient.age ? `${patient.age} años` : 'No registrada'} • 
                  {patient.gender === 'male' ? ' Masculino' :
                   patient.gender === 'female' ? ' Femenino' :
                   patient.gender === 'other' ? ' Otro' :
                   patient.gender === 'prefer_not_to_say' ? ' Prefiere no decir' : 
                   ' No registrado'}
                </InfoValue>
              </InfoContent>
            </InfoItem>
          </Grid>
        </Grid>
      </AppleCard>
      
      {/* Tarjeta de información médica */}
      <AppleCard>
        <SectionTitle>Información Médica</SectionTitle>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <SubSectionTitle>Datos Físicos</SubSectionTitle>
              <Grid container spacing={2}>
                {/* Siempre mostrar altura */}
                <Grid item xs={6}>
                  <InfoItem>
                    <InfoIcon>
                      <HeightIcon />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>Altura</InfoLabel>
                      <InfoValue>{patient.height ? `${patient.height} cm` : "No registrada"}</InfoValue>
                    </InfoContent>
                  </InfoItem>
                </Grid>
                
                {/* Siempre mostrar peso */}
                <Grid item xs={6}>
                  <InfoItem>
                    <InfoIcon>
                      <ScaleIcon />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>Peso</InfoLabel>
                      <InfoValue>{patient.weight ? `${patient.weight} kg` : "No registrado"}</InfoValue>
                    </InfoContent>
                  </InfoItem>
                </Grid>
                
                {/* Siempre mostrar circunferencia de cintura */}
                <Grid item xs={6}>
                  <InfoItem>
                    <InfoIcon>
                      <WaistIcon />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>Circunferencia de Cintura</InfoLabel>
                      <InfoValue>{patient.waistCircumference ? `${patient.waistCircumference} cm` : "No registrada"}</InfoValue>
                    </InfoContent>
                  </InfoItem>
                </Grid>
                
                {/* Siempre mostrar IMC */}
                <Grid item xs={6}>
                  <InfoItem>
                    <InfoIcon sx={{ 
                      bgcolor: patient.bmi ? (
                        parseFloat(patient.bmi) < 18.5 ? 'rgba(255, 193, 7, 0.1)' : 
                        parseFloat(patient.bmi) >= 18.5 && parseFloat(patient.bmi) < 25 ? 'rgba(76, 175, 80, 0.1)' : 
                        parseFloat(patient.bmi) >= 25 && parseFloat(patient.bmi) < 30 ? 'rgba(255, 152, 0, 0.1)' : 
                        'rgba(244, 67, 54, 0.1)'
                      ) : 'rgba(76, 175, 80, 0.1)',
                      color: patient.bmi ? (
                        parseFloat(patient.bmi) < 18.5 ? '#FFC107' : 
                        parseFloat(patient.bmi) >= 18.5 && parseFloat(patient.bmi) < 25 ? appleGreen : 
                        parseFloat(patient.bmi) >= 25 && parseFloat(patient.bmi) < 30 ? '#FF9800' : 
                        '#f44336'
                      ) : appleGreen
                    }}>
                      <ScaleIcon />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>IMC</InfoLabel>
                      <InfoValue>
                        {patient.bmi ? (
                          `${patient.bmi} kg/m² - ${
                            parseFloat(patient.bmi) < 18.5 ? 'Bajo peso' : 
                            parseFloat(patient.bmi) >= 18.5 && parseFloat(patient.bmi) < 25 ? 'Normal' :
                            parseFloat(patient.bmi) >= 25 && parseFloat(patient.bmi) < 30 ? 'Sobrepeso' :
                            'Obesidad'
                          }`
                        ) : "No registrado"}
                      </InfoValue>
                    </InfoContent>
                  </InfoItem>
                </Grid>
              </Grid>
            </Box>
            
            {/* Siempre mostrar tipo de sangre */}
            <InfoItem>
              <InfoIcon>
                <BloodtypeIcon />
              </InfoIcon>
              <InfoContent>
                <InfoLabel>Tipo de Sangre</InfoLabel>
                <InfoValue>{patient.bloodType || "No registrado"}</InfoValue>
              </InfoContent>
            </InfoItem>
            
            {/* Siempre mostrar tipo de diabetes */}
            <InfoItem>
              <InfoIcon>
                <HospitalIcon />
              </InfoIcon>
              <InfoContent>
                <InfoLabel>Tipo de Diabetes</InfoLabel>
                <InfoValue>
                  {patient.diabetesType ? (
                    patient.diabetesType === 'type1' ? 'Tipo 1' : 
                    patient.diabetesType === 'type2' ? 'Tipo 2' : 
                    patient.diabetesType === 'gestational' ? 'Gestacional' : 
                    patient.diabetesType === 'prediabetes' ? 'Prediabetes' : 'No especificado'
                  ) : "No registrado"}
                </InfoValue>
              </InfoContent>
            </InfoItem>
            
            {/* Factores de riesgo */}
            <Box sx={{ mt: 3 }}>
              <SubSectionTitle>Factores de Riesgo</SubSectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {/* Siempre mostrar cáncer */}
                  <InfoItem>
                    <InfoIcon sx={{ bgcolor: patient.cancer ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)', color: patient.cancer ? '#f44336' : appleGreen }}>
                      <WarningIcon />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>Cáncer</InfoLabel>
                      <InfoValue>{patient.cancer ? "El paciente tiene o ha tenido cáncer" : "No registrado"}</InfoValue>
                    </InfoContent>
                  </InfoItem>
                  
                  {/* Siempre mostrar tabaquismo */}
                  <InfoItem>
                    <InfoIcon sx={{ bgcolor: patient.smoking ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)', color: patient.smoking ? '#f44336' : appleGreen }}>
                      <SmokingRoomsIcon />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>Tabaquismo</InfoLabel>
                      <InfoValue>{patient.smoking ? "El paciente es fumador" : "No registrado"}</InfoValue>
                    </InfoContent>
                  </InfoItem>
                  
                  {/* Siempre mostrar alcohol */}
                  <InfoItem>
                    <InfoIcon sx={{ bgcolor: patient.alcohol ? 'rgba(255, 152, 0, 0.1)' : 'rgba(76, 175, 80, 0.1)', color: patient.alcohol ? '#FF9800' : appleGreen }}>
                      <AlcoholIcon />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>Consumo de Alcohol</InfoLabel>
                      <InfoValue>{patient.alcohol ? "El paciente consume alcohol regularmente" : "No registrado"}</InfoValue>
                    </InfoContent>
                  </InfoItem>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            {/* Signos vitales */}
            <Box sx={{ mb: 3 }}>
              <SubSectionTitle>Signos Vitales</SubSectionTitle>
              <Grid container spacing={2}>
                {/* Siempre mostrar presión arterial */}
                <Grid item xs={12}>
                  <InfoItem>
                    <InfoIcon>
                      <SpeedIcon />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>Presión Arterial</InfoLabel>
                      <InfoValue>
                        {(patient.systolicBP || patient.diastolicBP) ? 
                          `${patient.systolicBP || '--'} / ${patient.diastolicBP || '--'} mmHg` : 
                          "No registrada"}
                      </InfoValue>
                    </InfoContent>
                  </InfoItem>
                </Grid>
                
                {/* Siempre mostrar frecuencia cardíaca */}
                <Grid item xs={6}>
                  <InfoItem>
                    <InfoIcon>
                      <HeartIcon />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>Frecuencia Cardíaca</InfoLabel>
                      <InfoValue>{patient.heartRate ? `${patient.heartRate} lpm` : "No registrada"}</InfoValue>
                    </InfoContent>
                  </InfoItem>
                </Grid>
                
                {/* Siempre mostrar saturación de oxígeno */}
                <Grid item xs={6}>
                  <InfoItem>
                    <InfoIcon>
                      <OxygenIcon />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>Saturación de Oxígeno</InfoLabel>
                      <InfoValue>{patient.oxygenSaturation ? `${patient.oxygenSaturation}%` : "No registrada"}</InfoValue>
                    </InfoContent>
                  </InfoItem>
                </Grid>
                
                {/* Siempre mostrar temperatura */}
                <Grid item xs={6}>
                  <InfoItem>
                    <InfoIcon>
                      <TemperatureIcon />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>Temperatura</InfoLabel>
                      <InfoValue>{patient.temperature ? `${patient.temperature} °C` : "No registrada"}</InfoValue>
                    </InfoContent>
                  </InfoItem>
                </Grid>
                
                {/* Siempre mostrar glucosa en sangre */}
                <Grid item xs={6}>
                  <InfoItem>
                    <InfoIcon>
                      <MedicationIcon />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>Glucosa en Sangre</InfoLabel>
                      <InfoValue>{patient.bloodGlucose ? `${patient.bloodGlucose} mg/dL` : "No registrada"}</InfoValue>
                    </InfoContent>
                  </InfoItem>
                </Grid>
              </Grid>
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <SubSectionTitle>Enfermedades y Condiciones</SubSectionTitle>
              
              {/* Siempre mostrar patologías actuales */}
              <Box sx={{ mb: 2 }}>
                <InfoLabel>Patologías Actuales</InfoLabel>
                {patient.currentPathologies && patient.currentPathologies.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {patient.currentPathologies.map((pathology, index) => (
                      <AppleChip
                        key={index}
                        label={pathology}
                        color="warning"
                        size="medium"
                        icon={<HospitalIcon />}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No se han registrado patologías actuales
                  </Typography>
                )}
              </Box>
              
              {/* Siempre mostrar enfermedades crónicas */}
              <Box sx={{ mb: 2 }}>
                <InfoLabel>Enfermedades Crónicas</InfoLabel>
                {patient.chronicDiseases && patient.chronicDiseases.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {patient.chronicDiseases.map((disease, index) => (
                      <AppleChip
                        key={index}
                        label={disease}
                        color="primary"
                        size="medium"
                        icon={<MedicationIcon />}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No se han registrado enfermedades crónicas
                  </Typography>
                )}
              </Box>
              
              {/* Siempre mostrar alergias */}
              <Box sx={{ mb: 2 }}>
                <InfoLabel>Alergias</InfoLabel>
                {patient.allergies && patient.allergies.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {patient.allergies.map((allergy, index) => (
                      <AppleChip
                        key={index}
                        label={allergy}
                        color="warning"
                        size="medium"
                        icon={<WarningIcon />}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No se han registrado alergias
                  </Typography>
                )}
              </Box>
              
              {/* Siempre mostrar medicamentos */}
              <Box>
                <InfoLabel>Medicamentos</InfoLabel>
                {patient.medications && patient.medications.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {patient.medications.map((medication, index) => (
                      <AppleChip
                        key={index}
                        label={medication}
                        color="secondary"
                        size="medium"
                        icon={<MedicationIcon />}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No se han registrado medicamentos
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </AppleCard>
      
      {/* Tarjeta de notas médicas - Siempre mostrar, incluso si está vacía */}
      <AppleCard>
        <SectionTitle>Notas Médicas</SectionTitle>
        <Typography variant="caption" sx={{ color: '#86868b', display: 'block', mb: 2 }}>
          Estas notas solo son visibles para profesionales de la salud y la IA, nunca para el paciente.
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ 
          p: 2, 
          borderRadius: '10px', 
          bgcolor: 'rgba(76, 175, 80, 0.05)', 
          border: '1px solid rgba(76, 175, 80, 0.2)',
          mt: 2
        }}>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
            {patient.notes || "No se han registrado notas médicas para este paciente."}
          </Typography>
        </Box>
      </AppleCard>
    </Box>
  );
  
  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar compartido */}
      <Sidebar
        open={drawerOpen}
        isMobile={isMobile}
        onClose={handleDrawerToggle}
        onNewPatient={handleNewPatient}
        onLogout={handleLogout}
        activeSection="patients"
      />
      
      {/* Main Content */}
      <Main open={drawerOpen} isMobile={isMobile}>
        <DashboardHeader 
          title={`Paciente: ${patient ? `${patient.firstName} ${patient.lastName}` : 'Detalle'}`}
          user={user}
          isMobile={isMobile}
          onMenuClick={handleDrawerToggle}
          onLogout={handleLogout}
          onSettings={handleSettings}
        />
        
        {content}
      </Main>
      
      {/* Modal de paciente */}
      <PatientModal
        open={patientModal.open}
        onClose={handleClosePatientModal}
        patientId={patientModal.patientId}
      />
    </Box>
  );
};

export default PatientDetail;